import { createSubmissionBatch } from '../../../service';

// Only an opaque operation key is stored here. The server owns the durable
// routing plan; neither student answers nor file URLs belong in browser storage.
const storageKey = (assignmentId) => `jeddai:pending-bulk:${assignmentId}`;
export const pendingBatchKey = (assignmentId) => {
  try { return window.localStorage.getItem(storageKey(assignmentId)); } catch { return null; }
};
export const rememberPendingBatch = (assignmentId, key) => {
  try { window.localStorage.setItem(storageKey(assignmentId), key); } catch { /* Storage may be disabled. */ }
};
export const clearPendingBatch = (assignmentId, key) => {
  try {
    if (pendingBatchKey(assignmentId) === key) window.localStorage.removeItem(storageKey(assignmentId));
  } catch { /* Storage may be disabled. */ }
};
// These refusals reserve no plan; the teacher must change the routing before retrying.
const REFUSED_BEFORE_ANY_PLAN = new Set(['DRAFT_ALREADY_SUBMITTED', 'STUDENT_ALREADY_SUBMITTED']);
const NOTHING_RESERVED = new Set(['EMPTY_STUDENTS', 'DRAFT_ALREADY_SUBMITTED']);

export const isFinalBatchRejection = (error) =>
  error?.code === 'LOCAL_BATCH_VALIDATION_FAILED' ||
  error?.code === 'BATCH_ROUTING_CONFLICT' || error?.code === 'RESUBMISSION_NO_LONGER_ROUTABLE' ||
  REFUSED_BEFORE_ANY_PLAN.has(error?.code) || [400, 403, 404].includes(error?.status);

export const recoverPendingBatch = async (assignmentId, readRecoveredBatch = (batch) => batch) => {
  const key = pendingBatchKey(assignmentId);
  if (!key) return readRecoveredBatch(null);
  let recovered;
  try {
    // A replay uses the reserved server plan before validating a new payload.
    // If the original request never arrived, EMPTY_STUDENTS leaves the saved
    // draft-page snapshot in place for the teacher to continue editing.
    recovered = await createSubmissionBatch(assignmentId, { students: [] }, key);
  } catch (error) {
    if (isFinalBatchRejection(error)) {
      clearPendingBatch(assignmentId, key);
      if (NOTHING_RESERVED.has(error?.code)) return readRecoveredBatch(null);
    }
    throw error;
  }
  // A successful replay does not make the following snapshot/submission reads
  // reliable. Keep recovery durable across retries and reloads until those reads
  // succeed too. Their errors must not be mistaken for a rejected routing plan.
  const result = await readRecoveredBatch(recovered);
  clearPendingBatch(assignmentId, key);
  return result;
};
